/**
 * Opening a setup revision for the Playwright lane. Only the entry point is
 * seeded: which screen the revision falls back to, how far reachability is cut
 * and which status the store lands in all stay production behaviour, so a
 * screenshot of the reopened state shows what a user would actually see.
 */

import type { Screen } from '../types';
import {
  E2E_ACTIVE_DOCUMENT_NAME,
  E2E_ACTIVE_DOCUMENT_PATH,
  resolveActiveProjectKey,
} from '../hooks/useActiveDocumentName';
import { isSetupRevisionSettled } from '../lib/setupRevision';
import { useUIStore } from '../stores/useUIStore';
import type { E2eBootstrapConfig } from './e2eBootstrap';

/**
 * Reopens setup for the fixture document. A revision that is already open or
 * in flight is left alone — the store would otherwise allow a second open and
 * discard the revision the user is already working inside.
 *
 * @param boundaryScreen setup screen to hold; defaults to the previous
 * reachable screen, which is what returning from the current step produces.
 */
export function openE2eSetupRevision(boundaryScreen?: Screen): void {
  const projectKey = resolveActiveProjectKey({
    documentName: E2E_ACTIVE_DOCUMENT_NAME,
    documentPath: E2E_ACTIVE_DOCUMENT_PATH,
  });
  if (!projectKey) {
    return;
  }

  const { setupRevisionStatus, beginSetupRevision } = useUIStore.getState();
  if (!isSetupRevisionSettled(setupRevisionStatus)) {
    return;
  }

  beginSetupRevision({ projectKey, boundaryScreen });
}

/**
 * Seeds either the real reopened-setup transition or the otherwise transient
 * workspace state where an open revision still owns the navigation lock.
 */
export function applyE2eSetupRevisionFixture(config: E2eBootstrapConfig | null): void {
  if (config?.setupRevision === 'workspace-blocked') {
    const projectKey = resolveActiveProjectKey({
      documentName: E2E_ACTIVE_DOCUMENT_NAME,
      documentPath: E2E_ACTIVE_DOCUMENT_PATH,
    });
    if (!projectKey) {
      return;
    }

    // The real status is intentionally seeded without beginSetupRevision:
    // that action must leave Mapping, while this lane exercises feedback from
    // a repair action already mounted inside Validation.
    const state = useUIStore.getState();
    useUIStore.setState({
      setupRevisionId: state.setupRevisionId + 1,
      setupRevisionStatus: 'revisionOpen',
      setupRevisionProjectKey: projectKey,
      committedWorkflowStep: state.screen,
      pendingNavigationIntent: null,
    });
    return;
  }

  if (config?.setupRevision === 'reopened') {
    openE2eSetupRevision();
  }
}
